import React, { useMemo } from 'react';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import styled from 'styled-components';
import CarregandoTabela from '../../components/CarregandoTabela';
import { formatarDataHoraBr } from '../../Listagem/utils/dataBr';

// Branca: nada marcado aqui (só enviado). Azul: recebido. Verde: recebido e entregue.
// Cinza: carga ainda sem protocolo (só aparece na visão "todas as cargas").
export const COR_PROTOCOLO_RECEBIDO = '#bfdbfe';
export const COR_PROTOCOLO_ENTREGUE = '#86efac';
export const COR_SEM_PROTOCOLO = '#f1f5f9';
export const OPCOES_ITENS_POR_PAGINA = [50, 70, 100, 120];

export const corDoProtocolo = registro => {
  if (!registro?.protocolo) return COR_SEM_PROTOCOLO;
  if (registro?.protocolo_recebido && registro?.protocolo_entregue) return COR_PROTOCOLO_ENTREGUE;
  if (registro?.protocolo_recebido) return COR_PROTOCOLO_RECEBIDO;
  return null;
};

const descreverMarcacao = (rotulo, por, em) => {
  const quando = formatarDataHoraBr(em);
  return [rotulo, por ? `por ${por}` : null, quando ? `em ${quando}` : null].filter(Boolean).join(' ');
};

export const tituloDoProtocolo = registro => {
  if (!registro?.protocolo) return 'Sem protocolo: marque Protocolo na Programação Havan para seguir a emissão';
  const etapas = [
    registro?.protocolo_recebido ? descreverMarcacao('recebido', registro.protocolo_recebido_por, registro.protocolo_recebido_em) : null,
    registro?.protocolo_entregue ? descreverMarcacao('entregue', registro.protocolo_entregue_por, registro.protocolo_entregue_em) : null
  ].filter(Boolean);
  return etapas.length ? `Protocolo ${etapas.join(', ')}` : 'Protocolo enviado, aguardando recebimento';
};

export const descreverFrotas = registro => (registro.frotas?.length ? registro.frotas.join(', ') : registro.terceiro ? 'Terceiro' : '');

const texto = ({ getValue }) => getValue() || '-';

// Enviado vem da Programação Havan (marcação protocolo) e não muda aqui.
const renderEnviado = () => ({ row }) => {
  const enviado = Boolean(row.original.protocolo);
  const descricao = enviado
    ? `Protocolo ${row.original.ct} enviado (marcado na Programação Havan)`
    : `Carga ${row.original.ct} sem protocolo (marque na Programação Havan)`;
  return (
    <Marcacao title={descricao}>
      <input type="checkbox" checked={enviado} readOnly disabled aria-label={descricao} />
    </Marcacao>
  );
};

// Ordem das etapas: recebido e depois entregue. Entregue só com recebido; recebido não sai enquanto estiver entregue.
const renderEtapa = (etapa, { salvando = [], onAlternarEtapa } = {}) => ({ row }) => {
  const registro = row.original;
  const recebido = Boolean(registro.protocolo_recebido);
  const entregue = Boolean(registro.protocolo_entregue);
  const marcado = etapa === 'recebido' ? recebido : entregue;
  const gravando = salvando.includes(`${registro.id}:${etapa}`);
  const bloqueio = !registro.protocolo
    ? 'Sem protocolo: marque Protocolo na Programação Havan primeiro'
    : etapa === 'recebido'
    ? (entregue ? 'Desmarque o entregue antes de desmarcar o recebido' : null)
    : (!recebido ? 'Marque o recebido antes do entregue' : null);
  const rotulo = etapa === 'recebido' ? 'recebido' : 'entregue';
  const descricao = bloqueio || (marcado
    ? `${descreverMarcacao(`Protocolo ${registro.ct} ${rotulo}`, registro[`protocolo_${etapa}_por`], registro[`protocolo_${etapa}_em`])}. Clique para desmarcar.`
    : `Marcar o protocolo ${registro.ct} como ${rotulo}`);

  return (
    <Marcacao title={descricao} onClick={event => event.stopPropagation()}>
      <input
        type="checkbox"
        checked={marcado}
        disabled={gravando || Boolean(bloqueio) || !onAlternarEtapa}
        aria-label={descricao}
        onChange={event => onAlternarEtapa?.(registro, etapa, event.target.checked)}
      />
    </Marcacao>
  );
};

const ordenarMarcacao = campo => (a, b) => Number(Boolean(a.original[campo])) - Number(Boolean(b.original[campo]));

export const getEmissaoProtocoloColumns = config => [
  { id: 'ct', accessorKey: 'ct', header: 'C.T', size: 110, cell: texto },
  { id: 'filial_codigo', accessorKey: 'filial_codigo', header: 'Filial', size: 80, cell: ({ getValue }) => getValue() ?? '-' },
  { id: 'filial_nome', accessorKey: 'filial_nome', header: 'Nome', size: 220, cell: texto },
  { id: 'uf', accessorKey: 'uf', header: 'Estado', size: 80, cell: texto },
  { id: 'box', accessorKey: 'box', header: 'Box', size: 80, cell: texto },
  { id: 'inicio_carregamento', accessorKey: 'inicio_carregamento', header: 'H. cheg. veículo', size: 130, cell: texto },
  { id: 'termino_carregamento', accessorKey: 'termino_carregamento', header: 'H. término carga', size: 130, cell: texto },
  { id: 'frotas', accessorFn: descreverFrotas, header: 'Frota / Terceiro', size: 200, cell: texto },
  { id: 'enviado', header: 'Enviado', size: 95, enableSorting: false, enableCopy: false, cell: renderEnviado() },
  { id: 'recebido', accessorFn: registro => Boolean(registro.protocolo_recebido), header: 'Recebido', size: 95, enableCopy: false, sortingFn: ordenarMarcacao('protocolo_recebido'), cell: renderEtapa('recebido', config) },
  { id: 'entregue', accessorFn: registro => Boolean(registro.protocolo_entregue), header: 'Entregue', size: 95, enableCopy: false, sortingFn: ordenarMarcacao('protocolo_entregue'), cell: renderEtapa('entregue', config) }
];

const EmissaoProtocoloTable = ({
  id, titulo, chaveDados = '', data = [], carregando = false, erro = null, onRetry, salvando, onAlternarEtapa,
  mensagemVazia = 'Nenhuma carga marcada como protocolo neste turno.'
}) => {
  const columns = useMemo(() => getEmissaoProtocoloColumns({ salvando, onAlternarEtapa }), [onAlternarEtapa, salvando]);
  const protocolos = data.filter(registro => registro.protocolo);
  const recebidos = protocolos.filter(registro => registro.protocolo_recebido && !registro.protocolo_entregue).length;
  const entregues = protocolos.filter(registro => registro.protocolo_recebido && registro.protocolo_entregue).length;
  const pendentes = protocolos.length - recebidos - entregues;
  const semProtocolo = data.length - protocolos.length;

  return (
    <TableSection id={id}>
      <TurnoTitle>
        <span>{titulo}</span>
        {data.length ? (
          <Resumo aria-label="Resumo dos protocolos do turno">
            <Contagem><strong>{protocolos.length} protocolo(s)</strong></Contagem>
            <Contagem><Amostra $cor="#ffffff" aria-hidden="true" />{pendentes} aguardando</Contagem>
            <Contagem><Amostra $cor={COR_PROTOCOLO_RECEBIDO} aria-hidden="true" />{recebidos} recebido(s)</Contagem>
            <Contagem><Amostra $cor={COR_PROTOCOLO_ENTREGUE} aria-hidden="true" />{entregues} entregue(s)</Contagem>
            {semProtocolo ? <Contagem><Amostra $cor={COR_SEM_PROTOCOLO} aria-hidden="true" />{semProtocolo} sem protocolo</Contagem> : null}
          </Resumo>
        ) : null}
      </TurnoTitle>
      <TableWrap>
        {carregando && !data.length ? <CarregandoTabela texto="Carregando protocolos…" /> : null}
        {!carregando && erro ? (
          <TableState>
            <span>{erro}</span>
            <RetryButton type="button" onClick={onRetry}>Tentar novamente</RetryButton>
          </TableState>
        ) : null}
        {!carregando && !erro && !data.length ? <TableState>{mensagemVazia}</TableState> : null}
        {!erro && data.length > 0 ? (
          <TanStackTable
            key={chaveDados}
            columns={columns}
            data={data}
            isSortable
            pageSizeOptions={OPCOES_ITENS_POR_PAGINA}
            autoResetPageIndex={false}
            rowColorGetter={corDoProtocolo}
            rowTitleGetter={tituloDoProtocolo}
            enableCopy
            copyButtonPosition="start"
            className="emissao-protocolo-table"
          />
        ) : null}
      </TableWrap>
    </TableSection>
  );
};

export default EmissaoProtocoloTable;

const TableSection = styled.section`margin-bottom: 20px;`;
const TurnoTitle = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 28px; padding: 0 12px; border-radius: 2px 2px 0 0; background: #163455; color: #ffffff; font-size: 12px; font-weight: 600; text-transform: uppercase;`;
const Resumo = styled.span`display: inline-flex; flex-wrap: wrap; gap: 14px; text-transform: none; font-weight: 500;`;
const Contagem = styled.span`display: inline-flex; align-items: center; gap: 6px;`;
const Amostra = styled.span`display: inline-block; width: 12px; height: 12px; border: 1px solid rgba(255, 255, 255, 0.6); border-radius: 2px; background: ${({ $cor }) => $cor};`;
const TableWrap = styled.div`
  .emissao-protocolo-table { margin-top: 0 !important; }
  .emissao-protocolo-table > div { border-radius: 0 0 4px 4px; max-height: max(320px, calc(100vh - 230px)); }
  .emissao-protocolo-table thead { z-index: 30; }
  .emissao-protocolo-table th { background: #ffffff; color: #24344d; font-size: 12px; font-weight: 600; padding: 6px 8px; box-shadow: inset 0 -2px 0 #d7e0ea, 0 2px 4px rgba(15, 23, 42, 0.06); }
  .emissao-protocolo-table td { color: #1f2937; font-size: 13px; line-height: 1.2; padding: 4px 8px; }
`;
const Marcacao = styled.label`
  display: flex; justify-content: center; align-items: center; width: 100%; margin: 0; cursor: pointer;
  input { width: 18px; height: 18px; cursor: pointer; accent-color: #15803d; }
  input:disabled { cursor: not-allowed; }
`;
const TableState = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 88px; padding: 20px 12px; border: 1px solid #d7e0ea; border-top: 0; color: #526275; font-size: 13px;`;
const RetryButton = styled.button`padding: 6px 10px; border: 1px solid #163455; border-radius: 4px; background: #ffffff; color: #163455; font-size: 12px; font-weight: 600;`;
